'use client';

import { useState, useEffect, Suspense } from 'react';
import { ThemeProvider } from 'next-themes';
import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Toaster } from 'sonner';
import { AuthModal } from '@/components/features/auth';
import { AddressSheet, AddressRequiredDialog, SavedAddressAutofill } from '@/components/features/address';
import { CheckoutAddressGate } from '@/components/features/cart/checkout-address-gate';
import { CartSheet } from '@/components/features/cart';
import { ProductPopup } from '@/components/features/products';
import { VpnBanner } from '@/components/features/vpn-banner';
import { TechnicalBreakGuard } from '@/components/features/technical-break-guard';
import { YandexMapPreloader } from '@/components/features/address/yandex-map-preloader';
import { useAuthStore } from '@/stores/auth-store';
import { captureVkClickId, getTmrLvid, tmrSetUserId } from '@/lib/analytics';
import { CartProductSync } from '@/hooks/use-cart-product-sync';
import { AppErrorBoundary } from './app-error-boundary';
import { SourceTrackerInit } from './source-tracker-init';
import { MoneyRulesInit } from './money-rules-init';

interface ProvidersProps {
  children: React.ReactNode;
}

// Auth initializer + analytics setup
function AuthInitializer() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
    // VK: захватить rb_clickid из URL рекламы
    captureVkClickId();
    // Top.Mail.Ru: синхронизация userid для серверных конверсий
    const tmrLvid = getTmrLvid();
    if (tmrLvid) tmrSetUserId(tmrLvid);
  }, [checkAuth]);

  return null;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
            // Без явной политики TanStack повторяет запрос 3 раза, а axios внутри
            // делает ещё до 4 попыток — до 16 HTTP на один запрос. Когда база уже
            // не справляется, это превращает сбой в лавину.
            retry: (failureCount, error) => {
              if (!isAxiosError(error)) return false;
              const status = error.response?.status;
              // 401/403/404 и прочие 4xx — повтор ничего не изменит
              if (status && status >= 400 && status < 500) return false;
              // 503 — база моргнула, даём ровно одну попытку
              if (status === 503) return failureCount < 1;
              // остальные 5xx — сервер лёг, не добиваем
              if (status && status >= 500) return false;
              // сеть или таймаут — одна повторная попытка
              return failureCount < 1;
            },
          },
        },
      })
  );

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HeroUIProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem
            disableTransitionOnChange
          >
            <AuthInitializer />
            {/* SourceTrackerInit использует useSearchParams() — оборачиваем в Suspense,
                чтобы не вызывать CSR-bailout на prerender'е страниц (Next.js 16 требование). */}
            <Suspense fallback={null}>
              <SourceTrackerInit />
            </Suspense>
            <CartProductSync />
            {/* Пороги подарков, бесплатной доставки и коинов — с сервера,
                один источник вместо копий в коде витрины */}
            <MoneyRulesInit />
            <YandexMapPreloader />
            {children}
            <AuthModal />
            <AddressSheet />
            {/* Подставляет основной адрес из книги, пока гейт корзины не спросил
                его заново. Без этого сохранённые адреса не успевали примениться. */}
            <SavedAddressAutofill />
            {/* Объяснение «зачем адрес» — показывается вместо прежнего тоста */}
            <AddressRequiredDialog />
            <CartSheet />
            {/* Возврат в корзину после того, как адрес указан по требованию гейта */}
            <CheckoutAddressGate />
            <ProductPopup />
            {/* <VpnBanner /> — временно скрыт по запросу */}
            <TechnicalBreakGuard />
            <Toaster
              position="top-center"
              expand={false}
              visibleToasts={4}
              richColors
              theme="light"
              closeButton
              toastOptions={{
                duration: 4000,
              }}
            />
          </ThemeProvider>
        </HeroUIProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
